/**
 * Kalıcı öğe silme bağlamı.
 *
 * Silme eylemi kartlarda ve detay ekranında görünür; onay penceresi tek bir
 * yerde yaşar. Böylece her ekran kendi modal/pending/error durumunu
 * kopyalamaz ve aynı anda iki silme isteği başlatılamaz.
 */
import { createContext, type ReactNode, useContext, useState } from 'react';
import { api, type Item } from '../api.ts';
import { DeleteItemDialog } from './DeleteItemDialog.tsx';

export interface ItemDeleteValue {
  canDelete: boolean;
  requestDelete: (item: Item) => void;
}

const ItemDeleteContext = createContext<ItemDeleteValue>({
  canDelete: false,
  requestDelete: () => undefined,
});

export function useItemDelete(): ItemDeleteValue {
  return useContext(ItemDeleteContext);
}

export function ItemDeleteProvider({
  canDelete,
  onDeleted,
  children,
}: {
  canDelete: boolean;
  onDeleted?: ((item: Item) => void) | undefined;
  children: ReactNode;
}): ReactNode {
  const [target, setTarget] = useState<Item | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestDelete = (item: Item): void => {
    if (!canDelete) return;
    setError(null);
    setTarget(item);
  };

  const cancel = (): void => {
    if (busy) return;
    setTarget(null);
    setError(null);
  };

  const confirm = async (): Promise<void> => {
    if (target === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteItem(target.id);
      const deleted = target;
      setTarget(null);
      onDeleted?.(deleted);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Dosya silinemedi.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ItemDeleteContext.Provider value={{ canDelete, requestDelete }}>
      {children}
      {target !== null && (
        <DeleteItemDialog
          item={target}
          busy={busy}
          error={error}
          onConfirm={() => void confirm()}
          onCancel={cancel}
        />
      )}
    </ItemDeleteContext.Provider>
  );
}
